import React from "react";
import { useLanguage, useT } from "@/lib/LanguageContext";
import * as crmApi from "../../api/crmApi";
import { CHANNEL_KEYS, PURCHASE_KIND_KEYS, PURCHASE_STATUS_KEYS } from "../../contract";
import { fmtDate } from "../../format";
import useClientTab from "../../hooks/useClientTab";
import useCrmMoney from "../../hooks/useCrmMoney";
import EmptyState from "../../ui/EmptyState";
import CrmLoadFailure from "../../ui/CrmLoadFailure";
import CrmPager from "../../ui/CrmPager";
import { CRM } from "../../../../constants/testIds/crm";

/**
 * Purchases: every booking, order and enrolment auto-linked by email — date, item, type,
 * amount (net of refunds, gross shown when they differ), status, online/offline.
 */
export default function PurchasesTab({ clientId }) {
  const t = useT("crm");
  const { lang } = useLanguage();
  const money = useCrmMoney();
  const list = useClientTab(clientId, crmApi.listPurchases);
  if (list.loadError) return <CrmLoadFailure error={list.loadError} onRetry={list.reload} />;
  if (list.loaded && list.rows.length === 0) return <EmptyState title={t("purchases.empty")} />;
  return (
    <div className="overflow-x-auto" data-lenis-prevent>
      <table className="w-full min-w-[560px] text-sm text-eh-ink">
        <thead>
          <tr className="border-b border-eh-border-soft text-left text-xs uppercase tracking-wider text-eh-muted">
            <th scope="col" className="py-2 px-2 font-semibold">{t("purchases.date")}</th>
            <th scope="col" className="py-2 px-2 font-semibold">{t("purchases.item")}</th>
            <th scope="col" className="py-2 px-2 font-semibold">{t("purchases.type")}</th>
            <th scope="col" className="py-2 px-2 font-semibold">{t("purchases.amount")}</th>
            <th scope="col" className="py-2 px-2 font-semibold">{t("purchases.status")}</th>
          </tr>
        </thead>
        <tbody>
          {list.rows.map((p) => (
            <tr key={p.id} data-testid={CRM.clientPurchase(p.id)} className="border-b border-eh-border-soft">
              <td className="py-3 px-2 whitespace-nowrap">{fmtDate(p.occurred_at, lang)}</td>
              <td className="py-3 px-2">{p.item_name}</td>
              <td className="py-3 px-2">
                {PURCHASE_KIND_KEYS[p.kind] ? t(PURCHASE_KIND_KEYS[p.kind]) : "—"}
                {CHANNEL_KEYS[p.channel] && <span className="text-eh-muted"> · {t(CHANNEL_KEYS[p.channel])}</span>}
              </td>
              <td className="py-3 px-2 whitespace-nowrap">
                {money(p.net_cents, p.currency)}
                {p.refunded_cents > 0 && <span className="block text-xs text-eh-muted">{t("purchases.refunded", { amount: money(p.refunded_cents, p.currency) })}</span>}
              </td>
              <td className="py-3 px-2">{PURCHASE_STATUS_KEYS[p.status] ? t(PURCHASE_STATUS_KEYS[p.status]) : "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {list.total > list.pageSize && <CrmPager page={list.page} pageSize={list.pageSize} total={list.total} onPage={list.setPage} />}
    </div>
  );
}
